import { supabase } from '../lib/supabase';
import { openaiService } from './openaiService';

export const paymentService = {
  async createPaymentIntent(orderData, customerInfo) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: { orderData, customerInfo }
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create payment intent error:', error);
      throw error;
    }
  },

  async confirmPayment(paymentIntentId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
        body: { paymentIntentId }
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Confirm payment error:', error);
      throw error;
    }
  },

  async recordCashTransaction(transactionData) {
    try {
      const { data, error } = await supabase?.from('cash_transactions')?.insert([transactionData])?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Record cash transaction error:', error);
      throw error;
    }
  },

  async getActiveCashDrawer(tenantId) {
    try {
      const { data, error } = await supabase?.from('cash_drawers')?.select('*')?.eq('tenant_id', tenantId)?.eq('status', 'open')?.order('opened_at', { ascending: false })?.limit(1)?.maybeSingle();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get active cash drawer error:', error);
      throw error;
    }
  },

  async getCashTransactions(drawerId, filters = {}) {
    try {
      let query = supabase?.from('cash_transactions')?.select('*, created_by_profile:created_by(full_name)')?.eq('drawer_id', drawerId)?.order('created_at', { ascending: false });

      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.transactionType) {
        query = query?.eq('transaction_type', filters?.transactionType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get cash transactions error:', error);
      throw error;
    }
  },

  async getCashReconciliations(tenantId, filters = {}) {
    try {
      let query = supabase?.from('cash_reconciliations')?.select('*, drawer:drawer_id(drawer_name, location), reconciled_by_profile:reconciled_by(full_name)')?.eq('tenant_id', tenantId)?.order('reconciliation_date', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.drawerId) {
        query = query?.eq('drawer_id', filters?.drawerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get cash reconciliations error:', error);
      throw error;
    }
  },

  async getProcessorIntegrations(tenantId) {
    try {
      const { data, error } = await supabase?.from('payment_processor_integrations')?.select('*')?.eq('tenant_id', tenantId)?.eq('is_active', true);
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get processor integrations error:', error);
      throw error;
    }
  },

  async savePaymentMethod(paymentMethodData) {
    try {
      const { data, error } = await supabase?.from('member_payment_methods')?.insert([paymentMethodData])?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Save payment method error:', error);
      throw error;
    }
  },

  async saveGymPaymentMethod(memberId, tenantId) {
    try {
      const gymPaymentData = {
        tenant_id: tenantId,
        member_id: memberId,
        processor_type: 'other',
        processor_payment_method_id: `gym_payment_${memberId}_${Date.now()}`,
        payment_method_type: 'cash',
        is_default: false,
        is_active: true
      };
      
      const { data, error } = await supabase?.from('member_payment_methods')?.insert([gymPaymentData])?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Save gym payment method error:', error);
      throw error;
    }
  },

  async getPaymentMethods(memberId, tenantId) {
    try {
      const { data, error } = await supabase?.from('member_payment_methods')?.select('*')?.eq('member_id', memberId)?.eq('tenant_id', tenantId)?.eq('is_active', true)?.order('is_default', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get payment methods error:', error);
      throw error;
    }
  },

  async setDefaultPaymentMethod(paymentMethodId, memberId, tenantId) {
    try {
      await supabase?.from('member_payment_methods')?.update({ is_default: false })?.eq('member_id', memberId)?.eq('tenant_id', tenantId);

      const { data, error } = await supabase?.from('member_payment_methods')?.update({ is_default: true })?.eq('id', paymentMethodId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Set default payment method error:', error);
      throw error;
    }
  },

  async deletePaymentMethod(paymentMethodId) {
    try {
      const { error } = await supabase?.from('member_payment_methods')?.update({ is_active: false })?.eq('id', paymentMethodId);
      if (error) throw error;
    } catch (error) {
      console.error('Delete payment method error:', error);
      throw error;
    }
  },

  async getMembershipAlerts(tenantId, filters = {}) {
    try {
      let query = supabase?.from('membership_alerts')?.select('*, user_profiles:member_id(id, full_name, email, avatar_url), member_subscriptions:subscription_id(id, status, end_date)')?.eq('tenant_id', tenantId)?.order('created_at', { ascending: false });

      if (filters?.isRead !== undefined) query = query?.eq('is_read', filters?.isRead);
      if (filters?.isResolved !== undefined) query = query?.eq('is_resolved', filters?.isResolved);
      if (filters?.priority) query = query?.eq('priority', filters?.priority);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get membership alerts error:', error);
      throw error;
    }
  },

  async markAlertAsRead(alertId) {
    try {
      const { error } = await supabase?.from('membership_alerts')?.update({ is_read: true })?.eq('id', alertId);
      if (error) throw error;
    } catch (error) {
      console.error('Mark alert as read error:', error);
      throw error;
    }
  },

  async resolveAlert(alertId, userId, notes) {
    try {
      const { error } = await supabase?.from('membership_alerts')?.update({
          is_resolved: true,
          resolved_by: userId,
          resolved_at: new Date()?.toISOString(),
          resolution_notes: notes
        })?.eq('id', alertId);
      if (error) throw error;
    } catch (error) {
      console.error('Resolve alert error:', error);
      throw error;
    }
  },

  async getReconciliationData(tenantId, filters = {}) {
    try {
      let query = supabase?.from('transaction_reconciliation')?.select('*')?.eq('tenant_id', tenantId)?.order('transaction_date', { ascending: false });

      if (filters?.status) query = query?.eq('reconciliation_status', filters?.status);
      if (filters?.startDate) query = query?.gte('transaction_date', filters?.startDate);
      if (filters?.endDate) query = query?.lte('transaction_date', filters?.endDate);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Get reconciliation data error:', error);
      throw error;
    }
  },

  async updateReconciliationStatus(reconciliationId, status, userId, notes) {
    try {
      const { error } = await supabase?.from('transaction_reconciliation')?.update({
          reconciliation_status: status,
          reconciled_by: userId,
          notes: notes,
          updated_at: new Date()?.toISOString()
        })?.eq('id', reconciliationId);
      if (error) throw error;
    } catch (error) {
      console.error('Update reconciliation status error:', error);
      throw error;
    }
  },

  async getTransactionTrends(tenantId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('payment_intents')
        ?.select('amount, status, created_at')
        ?.eq('tenant_id', tenantId)
        ?.gte('created_at', startDate?.toISOString())
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const trendMap = {};
      data?.forEach((payment) => {
        const date = new Date(payment?.created_at)?.toISOString()?.split('T')?.[0];
        if (!trendMap?.[date]) {
          trendMap[date] = {
            date: new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            total_amount: 0,
            transaction_count: 0,
          };
        }
        if (payment?.status === 'succeeded') {
          trendMap[date].total_amount += parseFloat(payment?.amount || 0);
        }
        trendMap[date].transaction_count += 1;
      });

      return Object.values(trendMap);
    } catch (error) {
      console.error('Get transaction trends error:', error);
      throw error;
    }
  },

  async getRevenueForecast(tenantId) {
    try {
      // Get historical data for the last 90 days
      const historicalData = await this.getTransactionTrends(tenantId, 90);

      if (!historicalData || historicalData?.length === 0) {
        return null;
      }

      // Calculate statistics
      const totalRevenue = historicalData?.reduce((sum, d) => sum + (d?.total_amount || 0), 0);
      const avgDailyRevenue = totalRevenue / historicalData?.length;
      const totalTransactions = historicalData?.reduce((sum, d) => sum + (d?.transaction_count || 0), 0);
      const avgTransactionValue = totalRevenue / totalTransactions;

      // Prepare data for AI analysis
      const recentTrend = historicalData?.slice(-14); // Last 14 days
      const trendSummary = `Historical revenue data: Total revenue over ${historicalData?.length} days: $${totalRevenue?.toFixed(2)}, Average daily revenue: $${avgDailyRevenue?.toFixed(2)}, Total transactions: ${totalTransactions}, Average transaction value: $${avgTransactionValue?.toFixed(2)}. Recent 14-day trend: ${recentTrend?.map(d => `${d?.date}: $${d?.total_amount?.toFixed(2)} (${d?.transaction_count} txns)`)?.join(', ')}.`;

      // Use OpenAI for forecasting
      const forecastResponse = await openaiService?.generateRevenueForecast(trendSummary, {
        avgDailyRevenue,
        avgTransactionValue,
        totalTransactions,
        historicalDays: historicalData?.length,
      });

      return forecastResponse;
    } catch (error) {
      console.error('Get revenue forecast error:', error);
      throw error;
    }
  },

  async getPaymentMethodPerformance(tenantId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('payment_intents')
        ?.select('amount, status, processor_type, error_message, created_at')
        ?.eq('tenant_id', tenantId)
        ?.gte('created_at', startDate?.toISOString());

      if (error) throw error;

      // Group by payment method
      const performanceMap = {};
      data?.forEach((payment) => {
        const method = payment?.processor_type || 'unknown';
        if (!performanceMap?.[method]) {
          performanceMap[method] = {
            payment_method: method,
            total_transactions: 0,
            successful_transactions: 0,
            failed_transactions: 0,
            total_revenue: 0,
            average_transaction_amount: 0,
            recent_issues: [],
          };
        }

        performanceMap[method].total_transactions += 1;

        if (payment?.status === 'succeeded') {
          performanceMap[method].successful_transactions += 1;
          performanceMap[method].total_revenue += parseFloat(payment?.amount || 0);
        } else if (payment?.status === 'failed') {
          performanceMap[method].failed_transactions += 1;
          if (payment?.error_message && performanceMap?.[method]?.recent_issues?.length < 3) {
            performanceMap?.[method]?.recent_issues?.push(payment?.error_message);
          }
        }
      });

      // Calculate averages
      Object.values(performanceMap)?.forEach((method) => {
        method.average_transaction_amount =
          method?.successful_transactions > 0
            ? method?.total_revenue / method?.successful_transactions
            : 0;
      });

      return Object.values(performanceMap)?.sort((a, b) => b?.total_revenue - a?.total_revenue);
    } catch (error) {
      console.error('Get payment method performance error:', error);
      throw error;
    }
  },

  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  formatCardNumber(last4, brand) {
    const brandIcons = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover'
    };
    return `${brandIcons?.[brand?.toLowerCase()] || brand || 'Card'} •••• ${last4}`;
  }
};
